import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { apiService } from '../../utils/ApiService';
import { CmsMedia } from '../../data/cms';
export function useCms<T>(endpoint: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(Boolean(apiService.api_endpoint));
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setData(null); setError(false);
    if (!apiService.api_endpoint) { setLoading(false); return; }
    setLoading(true);
    apiService.get<T>(endpoint).then(result => { if (active) setData(result); }).catch(() => { if (active) setError(true); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [endpoint, attempt]);
  return { data, loading, error, retry: () => setAttempt(value => value + 1) };
}
export function CmsStatus({ loading, error, retry, empty }: { loading: boolean; error: boolean; retry: () => void; empty: string }) {
  return <div className="status-panel" role="status">{loading ? '読み込んでいます。' : error ? '情報を読み込めませんでした。時間をおいてもう一度お試しください。' : empty}{error && <div><button className="text-link" onClick={retry}>もう一度読み込む</button></div>}</div>;
}
export function HtmlContent({ html, className = 'page-prose' }: { html: string; className?: string }) {
  if (!/<\/?[a-z][\s\S]*>/i.test(html)) return <div className={`${className} cms-plain-text`}>{html}</div>;
  const content = DOMPurify.sanitize(html, { USE_PROFILES: { html: true }, RETURN_DOM_FRAGMENT: true });
  content.querySelectorAll('img, video, audio, source').forEach(element => {
    ['src', 'poster'].forEach(attribute => {
      const path = element.getAttribute(attribute);
      if (path && /^(?:\/|\.\/)?uploads\//.test(path)) element.setAttribute(attribute, imageUrl(path));
    });
  });
  const wrapper = document.createElement('div');
  wrapper.appendChild(content);
  return <div className={className} dangerouslySetInnerHTML={{ __html: wrapper.innerHTML }} />;
}
export function formatDate(value: string) {
  if (!value || Number.isNaN(Date.parse(value))) return '';
  return new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(value));
}
export function imageUrl(path?: string) {
  if (!path) return '';
  try {
    const base = new URL(`${apiService.api_endpoint.replace(/\/$/, '')}/`, window.location.origin);
    const url = new URL(path.replace(/^(?:\.\/|\/)?(?=uploads\/)/, ''), base);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch { return ''; }
}
export function safeLink(path?: string) {
  if (!path) return '';
  try { const url = new URL(path, window.location.origin); return ['http:', 'https:'].includes(url.protocol) ? url.href : ''; } catch { return ''; }
}
export function cardImageUrl(media?: CmsMedia | null) {
  return imageUrl(media?.formats?.medium?.url || media?.url);
}
export function Pagination({ page, pages, setPage }: { page: number; pages: number; setPage: (value: number) => void }) {
  if (pages < 2) return null;
  const numbers = Array.from({ length: pages }, (_, i) => i + 1).filter(number => number === 1 || number === pages || Math.abs(number - page) <= 2);
  return <nav className="pagination" aria-label="ページ選択"><button disabled={page === 1} onClick={() => setPage(page - 1)}>前へ</button>{numbers.map((number, i) => <span key={number}>{i > 0 && number - numbers[i - 1] > 1 && <span aria-hidden="true"> … </span>}<button aria-current={page === number ? 'page' : undefined} aria-label={`${number}ページ目`} onClick={() => setPage(number)}>{number}</button></span>)}<button disabled={page === pages} onClick={() => setPage(page + 1)}>次へ</button></nav>;
}
